import React, { useMemo, useState } from 'react';
import moment from 'moment';
import './WeeklyView.scss';
import { CONTENT_TYPE_ICONS } from 'constants/contentTypes';
import { ReactComponent as Other } from '../../assets/svg/Group.svg';
import { getUserImage, titleize } from 'helpers';
import ImageView from 'Components/Image/ImageView';
import KanbanDropDown from 'Components/KanbanCard/KanbanDropDown';

const WeeklyView = ({
  tasks,
  weekStartDate,
  onEdit,
  onDuplicate,
  onDelete,
  onMove,
  onArchive,
  handleEventClick,
  onCreatePost,
  onQuickUpdateDeadline,
  access,
}) => {
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [dropPosition, setDropPosition] = useState(null); // { dayKey, index }
  // Generate 7 days of the week starting from weekStartDate (Monday)
  const weekDays = useMemo(() => {
    const days = [];
    const startDate = new Date(weekStartDate);

    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startDate);
      currentDay.setDate(startDate.getDate() + i);
      days.push(currentDay);
    }
    return days;
  }, [weekStartDate]);

  // Group tasks by day
  const tasksByDay = useMemo(() => {
    const grouped = {};

    weekDays.forEach((day) => {
      const dayKey = moment(day).format('YYYY-MM-DD');
      grouped[dayKey] = [];
    });

    tasks.forEach((task) => {
      if (task.deadline_date) {
        const taskDate = moment(task.deadline_date).format('YYYY-MM-DD');
        if (grouped[taskDate]) {
          grouped[taskDate].push(task);
        }
      }
    });

    return grouped;
  }, [tasks, weekDays]);

  const formatDayHeader = (date) => {
    const dayName = moment(date).format('dddd');
    const dayNumber = moment(date).format('M/D');
    return { dayName, dayNumber };
  };

  const isToday = (date) => {
    return moment(date).isSame(moment(), 'day');
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleCardDragOver = (e, dayKey, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const cardHeight = rect.height;

    // Determine if we're in the top half or bottom half of the card
    const position = mouseY < cardHeight / 2 ? index : index + 1;

    setDragOverDay(dayKey);
    setDropPosition({ dayKey, index: position });
  };

  const handleColumnDragOver = (e, dayKey, tasksCount) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(dayKey);
    // If dragging over empty space in column, drop at the end
    setDropPosition({ dayKey, index: tasksCount });
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're leaving the column entirely
    if (e.currentTarget === e.target) {
      setDragOverDay(null);
      setDropPosition(null);
    }
  };

  const handleDrop = (e, newDate, dayKey) => {
    e.preventDefault();
    setDragOverDay(null);
    setDropPosition(null);

    if (draggedTask && onQuickUpdateDeadline) {
      const newDeadline = moment(newDate).format('YYYY-MM-DD');
      const oldDeadline = moment(draggedTask.deadline_date).format('YYYY-MM-DD');

      // Only update if the date has changed
      if (newDeadline !== oldDeadline) {
        // Call quick update (no drawer opening)
        onQuickUpdateDeadline(draggedTask.id, newDate);
      }

      setDraggedTask(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverDay(null);
    setDropPosition(null);
  };

  return (
    <div className="weekly-view">
      {/* Day Headers */}
      <div className="weekly-view__header">
        {weekDays.map((day, index) => {
          const { dayName, dayNumber } = formatDayHeader(day);
          const today = isToday(day);

          return (
            <div key={index} className="weekly-view__header-cell">
              <p className="weekly-view__day-name">
                {dayName} {dayNumber}
              </p>
              {today && (
                <div className="weekly-view__today-indicator" />
              )}
            </div>
          );
        })}
      </div>

      {/* Day Columns with Tasks */}
      <div className="weekly-view__body">
        {weekDays.map((day, index) => {
          const dayKey = moment(day).format('YYYY-MM-DD');
          const dayTasks = tasksByDay[dayKey] || [];

          return (
            <div
              key={index}
              className={`weekly-view__column ${
                dragOverDay === dayKey ? 'weekly-view__column--drag-over' : ''
              }`}
              onDragOver={(e) => handleColumnDragOver(e, dayKey, dayTasks.length)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day, dayKey)}
            >
              {/* Tasks for this day */}
              {dayTasks.map((task, taskIndex) => {
                const icon = CONTENT_TYPE_ICONS[task.content_type] || <Other />;
                const isDragging = draggedTask && draggedTask.id === task.id;
                const showInsertionBefore =
                  dropPosition &&
                  dropPosition.dayKey === dayKey &&
                  dropPosition.index === taskIndex;

                return (
                  <React.Fragment key={task.id}>
                    {/* Insertion indicator before card */}
                    {showInsertionBefore && (
                      <div className="weekly-view__insertion-indicator" />
                    )}

                    <div
                      className={`weekly-view__task-card ${
                        isDragging ? 'weekly-view__task-card--dragging' : ''
                      }`}
                      draggable={true}
                      onDragStart={(e) => handleDragStart(e, task)}
                      onDragOver={(e) => handleCardDragOver(e, dayKey, taskIndex)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleEventClick && handleEventClick(task)}
                    >
                    <div className="weekly-view__task-header">
                      <div className="weekly-view__task-content-type">
                        <div className="weekly-view__task-icon">{icon}</div>
                        <span className="weekly-view__task-type-text">
                          {task.content_type}
                        </span>
                      </div>
                      <KanbanDropDown
                        id={task.id}
                        onDelete={onDelete}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onMove={onMove}
                        onArchive={onArchive}
                        access={task.project_access}
                        state={task.state}
                      />
                    </div>
                    <p className="weekly-view__task-title">{task.title}</p>

                    {/* Status badge */}
                    {task.state && (
                      <div className={`weekly-view__task-status weekly-view__task-status--${task.state.replace(/_/g, '-')}`}>
                        {titleize(task.state)}
                      </div>
                    )}

                    {task.assignee && (
                      <div className="weekly-view__task-assignee">
                        {task.assignee.mini_avatar ? (
                          <ImageView
                            className="weekly-view__assignee-avatar"
                            src={task.assignee.mini_avatar}
                          />
                        ) : (
                          getUserImage(
                            task.assignee.mini_avatar,
                            task.assignee.name,
                            { width: 20, height: 20 },
                            undefined,
                            'weekly-view__assignee-avatar'
                          )
                        )}
                      </div>
                    )}
                  </div>
                  </React.Fragment>
                );
              })}

              {/* Insertion indicator after all cards (for dropping at the end) */}
              {dropPosition &&
                dropPosition.dayKey === dayKey &&
                dropPosition.index === dayTasks.length && (
                  <div className="weekly-view__insertion-indicator" />
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyView;
